import React from "react";
import { Pagination } from "@pd/pd-ui-kit";
import { useTranslation } from "react-i18next";

import TableControl from "~/pages/Editor/EditorInformationSystems/TableControl";
import Modal from "~/shared/ui/Fields/Modal";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import { useGetPersonalInformationQuery } from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { usePaginationParams } from "~/shared/hooks/usePaginationParams";
import { TableV2 } from "~/shared/ui/TableV2";
import { OpenModalActions } from "~/types/OpenModalActions";

import PersonalDataManagement from "./PersonalDataManagement";
import { getEditorPersonalDataColumns } from "./model/tableAdapter";
import { tableHeader } from "./options";
import { transformDataTable } from "./transformDataTable";
import { DataTableItem } from "./types";

import s from "./EditorPersonalData.less";

const EditorPersonalData = () => {
  const { t } = useTranslation();
  const setData = useSetLocationData();
  const { hasPermission } = usePermissions();
  const { openModal, count, page } = useGetLocationData();
  const { handleCount, handlePage } = usePaginationParams();

  const modalTitle = t(
    openModal === OpenModalActions.View
      ? "personal_data.view"
      : openModal === OpenModalActions.Add
        ? "personal_data.add"
        : openModal === OpenModalActions.Edit
          ? "personal_data.edit"
          : null,
  );

  const { data, refetch } = useGetPersonalInformationQuery({
    variables: {
      page: 0,
      count: 25,
    },
    fetchPolicy: "cache-and-network",
  });

  const total = data?.PositionGetter?.getPersonalInformation?.total;
  const tableItems = data?.PositionGetter?.getPersonalInformation?.items;
  const changedTableItems = transformDataTable(tableItems as DataTableItem[]);

  const closeModal = () => {
    setData("openModal", null, "currentId", null);
  };

  const handleRowEdit = (record: { id?: string | number }) => {
    setData("openModal", OpenModalActions.Edit, "currentId", record.id);
  };

  const handleViewDetails = (record: { id?: string | number }) => {
    setData("openModal", OpenModalActions.View, "currentId", record.id);
  };

  return (
    <div className={s.wrapper_edit}>
      <div className={s.header_edit}>
        <div>
          <span className={s.title_edit}>{t("info_system.pd")}</span>
        </div>

        <div>
          <TableControl createPermission="employeeAccounting.personal.info.create" />
          <Modal
            isOpen={Boolean(openModal)}
            title={modalTitle}
            actionClose={closeModal}
          >
            <PersonalDataManagement refetch={refetch} closeModal={closeModal} />
          </Modal>
        </div>
      </div>
      <div className={s.editor_table}>
        <TableV2
          columns={getEditorPersonalDataColumns(t, tableHeader)}
          dataSource={changedTableItems}
          onViewDetails={handleViewDetails}
          onRowEdit={
            hasPermission("employeeAccounting.personal.info.update")
              ? handleRowEdit
              : undefined
          }
          wrapperClassName={s.editor_table}
        />
        <Pagination
          count={count}
          page={page + 1}
          totalCount={total}
          onChangeCount={handleCount}
          onChangePage={handlePage}
        />
      </div>
    </div>
  );
};

export default EditorPersonalData;
